/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or operator can validate the order
  VALIDATE_ORDER: 'transition/validate-order',
  OPERATOR_VALIDATE_ORDER: 'transition/operator-validate-order',

  // Provider or operator can mark the product shipped
  MARK_SHIPPED: 'transition/mark-shipped',
  OPERATOR_MARK_SHIPPED: 'transition/operator-mark-shipped',

  // Operator can cancel the purchase before product has been marked as shipped
  CANCEL: 'transition/cancel',

  // Customer can dispute from purchased state, shipped state or received state
  DISPUTE_FROM_PURCHASED: 'transition/dispute-from-purchased',
  DISPUTE: 'transition/dispute',
  DISPUTE_FROM_RECEIVED: 'transition/dispute-from-received',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as resolved
  OPERATOR_MARK_RESOLVED_FROM_DISPUTED: 'transition/operator-mark-resolved-from-disputed',

  // Provider can mark the disputed transaction as resolved
  PROVIDER_MARK_RESOLVED_FROM_DISPUTED: 'transition/provider-mark-resolved-from-disputed',

  // Operator can mark the resolved transaction as completed
  MARK_COMPLETED_FROM_RESOLVED: 'transition/mark-completed-from-resolved',

  // Operator can mark the resolved transaction as partial refund
  MARK_PARTIAL_REFUND_FROM_RESOLVED: 'transition/mark-partial-refund-from-resolved',

  // Operator can mark the partial refund transaction as completed
  MARK_COMPLETED_FROM_PARTIAL_REFUND: 'transition/mark-completed-from-partial-refund',

  // Operator can mark the shipped transaction as received
  OPERATOR_MARK_RECEIVED: 'transition/operator-mark-received',

  // Operator can manually mark transaction as completed
  OPERATOR_MARK_COMPLETED: 'transition/operator-mark-completed',

  // System moves transaction automatically from received state to complete state
  // This makes it possible to to add notifications to that single transition.
  AUTO_COMPLETE: 'transition/auto-complete',

  // Reviews are given through transaction transitions by customer only
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  VALIDATED: 'validated',
  SHIPPED: 'shipped',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  RESOLVED: 'resolved',
  PARTIAL_REFUND: 'partial-refund',
  CANCELED: 'canceled',
  COMPLETED: 'completed',
  REVIEWED: 'reviewed',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-11',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        [transitions.VALIDATE_ORDER]: states.VALIDATED,
        [transitions.OPERATOR_VALIDATE_ORDER]: states.VALIDATED,
        [transitions.MARK_SHIPPED]: states.SHIPPED,
        [transitions.OPERATOR_MARK_SHIPPED]: states.SHIPPED,
        [transitions.DISPUTE_FROM_PURCHASED]: states.DISPUTED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.VALIDATED]: {
      on: {
        [transitions.MARK_SHIPPED]: states.SHIPPED,
        [transitions.OPERATOR_MARK_SHIPPED]: states.SHIPPED,
        [transitions.DISPUTE_FROM_PURCHASED]: states.DISPUTED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.SHIPPED]: {
      on: {
        [transitions.OPERATOR_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.DISPUTE_FROM_RECEIVED]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.OPERATOR_MARK_RESOLVED_FROM_DISPUTED]: states.RESOLVED,
        [transitions.PROVIDER_MARK_RESOLVED_FROM_DISPUTED]: states.RESOLVED,
      },
    },

    [states.RESOLVED]: {
      on: {
        [transitions.MARK_COMPLETED_FROM_RESOLVED]: states.COMPLETED,
        [transitions.MARK_PARTIAL_REFUND_FROM_RESOLVED]: states.PARTIAL_REFUND,
      },
    },

    [states.PARTIAL_REFUND]: {
      on: {
        [transitions.MARK_COMPLETED_FROM_PARTIAL_REFUND]: states.COMPLETED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.OPERATOR_MARK_COMPLETED]: states.COMPLETED,
        [transitions.AUTO_COMPLETE]: states.COMPLETED,
      },
    },

    [states.COMPLETED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED,
      },
    },

    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.VALIDATE_ORDER,
    transitions.OPERATOR_VALIDATE_ORDER,
    transitions.CANCEL,
    transitions.MARK_SHIPPED,
    transitions.OPERATOR_MARK_SHIPPED,
    transitions.OPERATOR_MARK_RECEIVED,
    transitions.DISPUTE_FROM_PURCHASED,
    transitions.DISPUTE,
    transitions.DISPUTE_FROM_RECEIVED,
    transitions.OPERATOR_DISPUTE,
    transitions.OPERATOR_MARK_RESOLVED_FROM_DISPUTED,
    transitions.PROVIDER_MARK_RESOLVED_FROM_DISPUTED,
    transitions.MARK_COMPLETED_FROM_RESOLVED,
    transitions.MARK_PARTIAL_REFUND_FROM_RESOLVED,
    transitions.MARK_COMPLETED_FROM_PARTIAL_REFUND,
    transitions.OPERATOR_MARK_COMPLETED,
    transitions.AUTO_COMPLETE,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = transition => {
  return false; // Provider reviews have been removed
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [transitions.REQUEST_PAYMENT, transitions.REQUEST_PAYMENT_AFTER_INQUIRY].includes(
    transition
  );
};

// Check when transaction is completed (item is received and review notifications sent)
// Note: Payout is created during AUTO_COMPLETE and OPERATOR_MARK_COMPLETED transitions
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.OPERATOR_MARK_COMPLETED,
    transitions.AUTO_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.EXPIRE_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PURCHASED, states.VALIDATED];

// Operator-only transitions. MUST mirror the server's authoritative actor map
// (server/api-util/txEngine/stateMachine.js `TRANSITION_ACTORS`). Used by the
// admin transaction UI to render the operator actions; the server re-checks the
// actor, so this list only drives which buttons are offered.
export const operatorTransitions = [
  transitions.OPERATOR_VALIDATE_ORDER,
  transitions.OPERATOR_MARK_SHIPPED,
  transitions.CANCEL,
  transitions.OPERATOR_DISPUTE,
  transitions.CANCEL_FROM_DISPUTED,
  transitions.OPERATOR_MARK_RESOLVED_FROM_DISPUTED,
  transitions.MARK_COMPLETED_FROM_RESOLVED,
  transitions.MARK_PARTIAL_REFUND_FROM_RESOLVED,
  transitions.MARK_COMPLETED_FROM_PARTIAL_REFUND,
  transitions.OPERATOR_MARK_RECEIVED,
  transitions.OPERATOR_MARK_COMPLETED,
];

// Operator transitions legal from the given state (intersect the process graph's
// outgoing transitions with the operator set). Returns transition name strings.
export const getAvailableOperatorTransitions = state => {
  const stateDef = graph.states[state];
  if (!stateDef || !stateDef.on) {
    return [];
  }
  return Object.keys(stateDef.on).filter(t => operatorTransitions.includes(t));
};
